import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let extSeq = 0;

export const makeClientEntry = ({ id, label, email }) => ({
  external: false,
  id,
  label: label || `Client #${id}`,
  email: email || "",
});

export const makeExternalEntry = ({ name, email, phone }) => ({
  external: true,
  key: `e_${extSeq++}`,
  name: (name || "").trim(),
  email: (email || "").trim(),
  phone: (phone || "").trim(),
  label: (name || "").trim() || (email || "").trim(),
});

export const entryKey = (o) => (o.external ? o.key : `c${o.id}`);

const EMPTY_DRAFT = { name: "", email: "", phone: "" };

const AppointmentParticipantsEditor = ({
  clientOptions = [],
  value = [],
  onChange,
  searchInputValue,
  onSearchInputChange,
  searchLoading = false,
  listboxProps,
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [draftError, setDraftError] = useState("");

  const selectedKeys = new Set(value.map(entryKey));

  const options = [
    ...clientOptions.map((c) => makeClientEntry(c)),
    ...value.filter((v) => v.external),
  ];

  const setDraftField = (field) => (e) => {
    setDraft((prev) => ({ ...prev, [field]: e.target.value }));
    setDraftError("");
  };

  const cancelExternal = () => {
    setAdding(false);
    setDraft({ ...EMPTY_DRAFT });
    setDraftError("");
  };

  const addExternal = () => {
    const name = draft.name.trim();
    const email = draft.email.trim();
    if (!name || !email) {
      setDraftError("Name and email are required.");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setDraftError("Enter a valid email.");
      return;
    }
    const exists = value.some(
      (v) => v.external && v.email && v.email.toLowerCase() === email.toLowerCase()
    );
    if (exists) {
      setDraftError("That person is already added.");
      return;
    }
    onChange([...value, makeExternalEntry({ name, email, phone: draft.phone })]);
    cancelExternal();
  };

  return (
    <Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        filterOptions={onSearchInputChange ? (x) => x : undefined}
        loading={searchLoading}
        options={options}
        value={value}
        onChange={(_, next) => onChange(next)}
        {...(onSearchInputChange
          ? {
              inputValue: searchInputValue,
              onInputChange: (_, newInputValue, reason) => {
                if (reason === "reset") return;
                onSearchInputChange(newInputValue);
              },
            }
          : {})}
        getOptionLabel={(o) => (o.external ? o.label || o.email : o.label)}
        isOptionEqualToValue={(o, v) => entryKey(o) === entryKey(v)}
        getOptionDisabled={(o) => selectedKeys.has(entryKey(o))}
        renderOption={(props, option) => (
          <li {...props} key={entryKey(option)}>
            {option.label}
            {!option.external && option.email ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {option.email}
              </Typography>
            ) : null}
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={entryKey(option)}
              label={option.external ? `${option.label} (not in system)` : option.label}
              color={option.external ? "secondary" : "default"}
              variant={option.external ? "outlined" : "filled"}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Clients *"
            placeholder={value.length ? "" : "Search and select clients…"}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        ListboxProps={{ style: { maxHeight: 320 }, ...listboxProps }}
        noOptionsText={
          searchLoading
            ? "Searching…"
            : onSearchInputChange
            ? "Type to search clients"
            : "No matching clients"
        }
      />

      {!adding && (
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setAdding(true)}
          sx={{ mt: 1, display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 500 }}
        >
          <PersonAddOutlinedIcon sx={{ fontSize: 16 }} />
          Client not in the system? Add their details
        </Link>
      )}

      <Collapse in={adding}>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mt: 1,
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "action.hover" : "grey.50"),
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Add a client not in the system
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Used for this appointment only — not added to your client list.
          </Typography>
          <Stack spacing={1.5}>
            <TextField
              label="Full name *"
              size="small"
              fullWidth
              value={draft.name}
              onChange={setDraftField("name")}
              autoFocus
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Email *"
                type="email"
                size="small"
                fullWidth
                value={draft.email}
                onChange={setDraftField("email")}
              />
              <TextField
                label="Phone"
                size="small"
                fullWidth
                value={draft.phone}
                onChange={setDraftField("phone")}
              />
            </Stack>
            {draftError && (
              <Typography variant="caption" color="error">
                {draftError}
              </Typography>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={cancelExternal}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={addExternal}>
                Add client
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default AppointmentParticipantsEditor;
